import React, { useEffect, useState } from 'react';
import { Tooltip } from 'antd';
import { formatMsg, timeutil } from 'sharedresources';
import { getSlotTimerState } from '../../util/slotTimer';
import styles from './style.module.css';

// Minutes only, so a 30s tick is enough; the pill re-renders on its own, not the whole table.
const TICK_MS = 30000;

interface Props {
    record: { date: number; startTime: number; endTime: number };
    attendance: Record<string, any> | null | undefined;
    status: string;
    settings: Record<string, any> | null | undefined;
    // Parents see only the countdown; the end time is for staff.
    showEndsAt?: boolean;
    className?: string;
}

const SlotTimerPill = ({ record, attendance, status, settings, showEndsAt = true, className = '' }: Props) => {
    // Read the clock on every render: a time held from mount goes stale while the pill is
    // hidden, so rows marked together would start from different times.
    const [, setTick] = useState(0);
    const state = getSlotTimerState(record, attendance, status, settings, timeutil.getEpoch());
    const isActive = !!state;

    useEffect(() => {
        if (!isActive) return;
        const id = setInterval(() => setTick((t) => t + 1), TICK_MS);
        return () => clearInterval(id);
    }, [isActive]);

    if (!state) return null;

    const endsAt = showEndsAt
        ? formatMsg('slotTimer.endsAt', { time: timeutil.getInstance(state.endsAt).format('hh:mm A') })
        : undefined;

    if (state.kind === 'timesUp') {
        return (
            <Tooltip title={endsAt}>
                <span className={`${styles.slotTimerPill} ${styles.slotTimerTimesUp} ${className}`} data-testid='slot-timer-pill'>
                    {formatMsg('slotTimer.timesUp')}
                </span>
            </Tooltip>
        );
    }

    const tone = state.minsLeft <= 5 ? styles.slotTimerUrgent : state.minsLeft <= 10 ? styles.slotTimerWarning : styles.slotTimerRunning;

    return (
        <Tooltip title={endsAt}>
            <span className={`${styles.slotTimerPill} ${tone} ${className}`} data-testid='slot-timer-pill'>
                {formatMsg('slotTimer.minLeft', { count: state.minsLeft })}
            </span>
        </Tooltip>
    );
};

export default SlotTimerPill;
